/**
 * Gift Service
 * Flutter source: lib/presentation/bottom_sheet/send_gift_bottom_sheet.dart
 *
 * Loads available gifts from local SecureStorage and builds the
 * AppTransaction + AppNotification payloads required by the `sendGift`
 * cloud function.
 */
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import type { GiftStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { AppTransactionDto } from '@/data/dtos/transaction/appTransactionDto';
import type { AppNotificationDto } from '@/data/dtos/notification/appNotificationDto';
import type { UsersInfoDto } from '@/data/dtos/info/usersInfoDto';
import type { ChatInfoDto } from '@/data/dtos/info/chatInfoDto';
import type { ChatVideoCallInfoDto } from '@/data/dtos/info/chatVideoCallInfoDto';
import type { BroadcastingInfoDto } from '@/data/dtos/info/broadcastingInfoDto';
import type { GiftInfoDto } from '@/data/dtos/info/giftInfoDto';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { BroadcastingInfoRemapper } from '@/domain/remappers/info/broadcastingInfoRemapper';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { GiftRemapper } from '@/domain/remappers/gift/giftRemapper';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { UserUtils } from '@/domain/utils/userUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface SendGiftParams {
  media: UserMediaEntity;
  currentUser: UserEntity;
  gift: GiftEntity;
  instances: number;
}

export interface SendChatGiftParams {
  currentUser: UserEntity;
  peerUser: UserEntity;
  gift: GiftEntity;
  instances: number;
}

export interface SendVideoCallGiftParams {
  currentUser: UserEntity;
  callerUser: UserEntity;
  audienceUser: UserEntity;
  callMessageId: string;
  gift: GiftEntity;
  instances: number;
}

export interface SendLiveStreamingGiftParams {
  currentUser: UserEntity;
  broadcasterFirebaseId: string;
  broadcasterName: string;
  liveStreamingMessageId: string;
  gift: GiftEntity;
  instances: number;
}

export class GiftService {
  private readonly storage = SecureStorageWebCrypto.getInstance();

  /**
   * Load all locally cached gifts and sort by `order` (fallback to `coins`).
   * Flutter parity: send_gift_bottom_sheet.dart:_getLocalGifts
   */
  async getLocalGifts(): Promise<GiftEntity[]> {
    const stored = await this.storage.getAllWithPrefix<GiftStorage>(SECURE_STORAGE_KEYS.GIFTS);
    const gifts = GiftRemapper.toEntityList(stored);

    const allHaveOrder = gifts.every(g => g.order != null);
    if (allHaveOrder) {
      gifts.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    } else {
      gifts.sort((a, b) => a.coins - b.coins);
    }

    return gifts;
  }

  /**
   * Build the shared `giftInfo` sub-object used by every send path
   * (content transaction + notification, chat transaction).
   * `coins` is stamped as the per-instance cost times the count, matching
   * Flutter's `send_gift_bottom_sheet.dart` giftInfo stamping.
   */
  private buildGiftInfo(gift: GiftEntity, instances: number): GiftInfoDto {
    return {
      name: gift.name,
      thumbnail: gift.image,
      coins: gift.coins * instances,
      instances,
    };
  }

  /**
   * Build the AppTransaction + AppNotification payloads and call `sendGift` CF.
   * Flutter parity: send_gift_bottom_sheet.dart:_sendGift +
   *                 content_list_pager_widget.dart:_onOpenSendContentGiftSheet
   */
  async executeSendGift(params: SendGiftParams): Promise<boolean> {
    const { media, currentUser, gift, instances } = params;

    if (!media.firebaseId) {
      appLogger.error('executeSendGift called with missing media firebaseId', 'GiftService.executeSendGift', {
        error: new Error('Missing media.firebaseId'),
        context: { mediaId: media.id, gift: gift.name, instances },
      });
      return false;
    }

    const now = Date.now();

    const giftInfo = this.buildGiftInfo(gift, instances);

    const mediaInfo = {
      mediaId: media.id,
      firebaseId: media.firebaseId,
      thumbnail: media.relativeThumbnailPath ?? '',
      path: media.relativePath ?? '',
      type: media.type ?? 0,
      duration: media.duration,
      aspectRatio: media.aspectRatio,
      allowUnlock: media.allowUnlock,
    };

    const usersInfo = {
      userId: currentUser.firebaseId,
      peerUserId: media.firebaseId,
      usersIds: [currentUser.firebaseId, media.firebaseId],
      name: currentUser.name,
      username: currentUser.username,
      peerName: media.userName ?? '',
    };

    const appTransaction: AppTransactionDto = {
      usersInfo,
      transactionType: AppTransactionType.gift,
      mediaInfo,
      giftInfo,
      createdDate: now,
      updatedDate: now,
    };

    const notification: AppNotificationDto = {
      info: {
        usersInfo: {
          userId: currentUser.firebaseId,
          peerUserId: media.firebaseId,
          name: currentUser.name,
          peerName: media.userName ?? '',
        },
        notificationType: AppNotificationType.contentGift,
        mediaInfo,
        giftInfo,
        timestamp: now,
      },
    };

    return cloudFunctionsService.sendGift(appTransaction, notification, null);
  }

  /**
   * Build the chat-variant AppTransaction and call `sendGift` CF.
   *
   * Differs from the content path (`executeSendGift`) in three ways, all
   * dictated by Flutter:
   *  - carries `chatInfo` (`description = "Gift"`) and NO `mediaInfo`
   *    (`chat_screen.dart:3658-3666`);
   *  - sends a `null` notification — "do not send a notification for chat or
   *    live gifts" (`send_gift_bottom_sheet.dart:199-200`);
   *  - passes a computed `groupChatId` because `chatInfo != null`
   *    (`gifts_repository.dart:19-27`).
   *
   * Flutter parity: chat_screen.dart:3642-3673 (transaction shape) +
   *                 send_gift_bottom_sheet.dart:159-223 (giftInfo stamping).
   */
  async executeSendChatGift(params: SendChatGiftParams): Promise<boolean> {
    const { currentUser, peerUser, gift, instances } = params;

    const now = Date.now();

    const giftInfo = this.buildGiftInfo(gift, instances);

    // Flutter's chat handler (chat_screen.dart:3647-3656) sets only userId,
    // name, peerUserId, usersIds and peerName — it deliberately omits
    // `username`, unlike the content path (executeSendGift above).
    const usersInfo: UsersInfoDto = {
      userId: currentUser.firebaseId,
      peerUserId: peerUser.firebaseId,
      usersIds: [currentUser.firebaseId, peerUser.firebaseId],
      name: currentUser.name,
      peerName: peerUser.name,
    };

    // Literal server-side transaction descriptor (chat_screen.dart:3659) — not
    // user-visible copy, so it is intentionally not l10n'd.
    const chatInfo: ChatInfoDto = { description: 'Gift' };

    const appTransaction: AppTransactionDto = {
      usersInfo,
      chatInfo,
      transactionType: AppTransactionType.gift,
      giftInfo,
      createdDate: now,
      updatedDate: now,
    };

    // chatInfo != null → compute groupChatId (gifts_repository.dart:19-27).
    const groupChatId = ChatUtils.getGroupChatId(
      currentUser.firebaseId,
      peerUser.firebaseId,
    );

    // notification is null for chat gifts (send_gift_bottom_sheet.dart:199-200).
    return cloudFunctionsService.sendGift(appTransaction, null, groupChatId);
  }

  /**
   * Build the video-call-variant AppTransaction and call `sendGift` CF.
   *
   * Differs from both other paths: it carries `chatVideoCallInfo` and NO
   * `mediaInfo` / NO `chatInfo`. Like the chat path it sends a `null`
   * notification ("do not send a notification for chat or live gifts",
   * `send_gift_bottom_sheet.dart:199-200`) and passes a computed `groupChatId`
   * (required because `chatVideoCallInfo != null`, `gifts_repository.dart:20-27`).
   *
   * Flutter parity: video_call_screen.dart:1170-1195 (peer-resolution +
   *                 transaction shape) + send_gift_bottom_sheet.dart:159-223
   *                 (giftInfo stamping).
   */
  async executeSendVideoCallGift(params: SendVideoCallGiftParams): Promise<boolean> {
    const { currentUser, callerUser, audienceUser, callMessageId, gift, instances } = params;

    // Peer-resolution: the peer is the *other* party in the call. The current
    // user may be the caller OR the audience, so do not assume the caller is
    // always the current user (video_call_screen.dart:1170-1173).
    const peerUser =
      callerUser.firebaseId === currentUser.firebaseId ? audienceUser : callerUser;

    const now = Date.now();

    const giftInfo = this.buildGiftInfo(gift, instances);

    // `peerUsername` is set from `peerUser.name`, NOT `peerUser.username`
    // (video_call_screen.dart:1178).
    const chatVideoCallInfo: ChatVideoCallInfoDto = {
      videoCallMessageId: callMessageId,
      peerUserId: peerUser.firebaseId,
      peerUsername: peerUser.name,
      peerUserProfile: UserUtils.generateUserProfileImageUrl(peerUser.firebaseId),
    };

    // Flutter's video-call handler (video_call_screen.dart:1183-1189) sets only
    // userId, name, peerUserId, usersIds and peerName — it deliberately omits
    // `username` (like the chat path) and does not set `peerUsername` here.
    const usersInfo: UsersInfoDto = {
      userId: currentUser.firebaseId,
      peerUserId: peerUser.firebaseId,
      usersIds: [currentUser.firebaseId, peerUser.firebaseId],
      name: currentUser.name,
      peerName: peerUser.name,
    };

    // No mediaInfo, no chatInfo on video-call gifts (video_call_screen.dart:1191-1195).
    const appTransaction: AppTransactionDto = {
      usersInfo,
      chatVideoCallInfo,
      transactionType: AppTransactionType.gift,
      giftInfo,
      createdDate: now,
      updatedDate: now,
    };

    // chatVideoCallInfo != null → compute groupChatId (gifts_repository.dart:20-27).
    const groupChatId = ChatUtils.getGroupChatId(
      currentUser.firebaseId,
      peerUser.firebaseId,
    );

    // notification is null for video-call gifts (video_call_screen.dart:1201 +
    // send_gift_bottom_sheet.dart:199-200).
    return cloudFunctionsService.sendGift(appTransaction, null, groupChatId);
  }

  /**
   * Build the live-streaming-variant AppTransaction and call `sendGift` CF.
   *
   * The audience tips the broadcaster during a live session. Structurally the
   * twin of the video-call path: media-less, chat-less, carrying a
   * `broadcastingInfo` sub-object instead of `chatVideoCallInfo`. Like the
   * chat/video-call paths it sends a `null` notification ("do not send a
   * notification for chat or live gifts", send_gift_bottom_sheet.dart:199-200)
   * and passes a computed `groupChatId` (required because `broadcastingInfo !=
   * null`, gifts_repository.dart:20-27).
   *
   * The audience build (live_streaming_screen.dart:1412-1417) sets only two Dart
   * fields on the `BroadcastingInfo` — `timestamp` and `liveStreamingMessageId`
   * — but the constructor still defaults `broadcasterStreamingId = -1`
   * (broadcasting_info.dart:12) and leaves `isLive` null, and Flutter serializes
   * the whole transaction via `transaction.toJson()` (gifts_repository.dart:29),
   * so the *serialized* sub-object carries all four keys
   * (`_$BroadcastingInfoToJson`, broadcasting_info.g.dart:17-23). Because
   * `cloudFunctionsService.sendGift` passes the `AppTransactionDto` directly to
   * the callable (no recursive remapper at the boundary), the DTO's
   * `broadcastingInfo` must already be the 4-key wire object — so we serialize it
   * through `BroadcastingInfoRemapper.toJson`, which emits
   * `{ liveStreamingMessageId, broadcasterStreamingId: -1, timestamp, isLive: null }`.
   *
   * Flutter parity: live_streaming_screen.dart:1397-1430 (transaction shape) +
   *                 broadcasting_info.g.dart:17-23 (wire shape) +
   *                 send_gift_bottom_sheet.dart:159-208 (giftInfo stamping).
   */
  async executeSendLiveStreamingGift(params: SendLiveStreamingGiftParams): Promise<boolean> {
    const { currentUser, broadcasterFirebaseId, broadcasterName, liveStreamingMessageId, gift, instances } = params;

    const now = Date.now();

    const giftInfo = this.buildGiftInfo(gift, instances);

    // Flutter's audience build (live_streaming_screen.dart:1401-1410) sets only
    // userId, name, peerUserId, usersIds and peerName — it deliberately omits
    // `username` (like the chat/video-call paths).
    const usersInfo: UsersInfoDto = {
      userId: currentUser.firebaseId,
      peerUserId: broadcasterFirebaseId,
      usersIds: [currentUser.firebaseId, broadcasterFirebaseId],
      name: currentUser.name,
      peerName: broadcasterName,
    };

    // Serialize the broadcastingInfo through BroadcastingInfoRemapper.toJson so
    // the wire payload carries all four keys matching Flutter's
    // `_$BroadcastingInfoToJson` (broadcasting_info.g.dart:17-23):
    // `{ liveStreamingMessageId, broadcasterStreamingId: -1, timestamp, isLive: null }`.
    // A bare 2-key `{ liveStreamingMessageId, timestamp }` DTO would silently
    // drop `broadcasterStreamingId`/`isLive` because `sendGift` passes the DTO
    // straight to the callable (no recursive remapper). The remapper returns a
    // JsonObject with a literal `isLive: null` — a value `BroadcastingInfoDto`
    // cannot hold — so it is cast onto the field at the serialize boundary.
    const broadcastingInfo = BroadcastingInfoRemapper.toJson({
      liveStreamingMessageId,
      broadcasterStreamingId: -1,
      timestamp: now,
    }) as unknown as BroadcastingInfoDto;

    // No mediaInfo, no chatInfo, no chatVideoCallInfo on live-streaming gifts
    // (live_streaming_screen.dart:1419-1423).
    const appTransaction: AppTransactionDto = {
      usersInfo,
      broadcastingInfo,
      transactionType: AppTransactionType.gift,
      giftInfo,
      createdDate: now,
      updatedDate: now,
    };

    // broadcastingInfo != null → compute groupChatId (gifts_repository.dart:20-27).
    const groupChatId = ChatUtils.getGroupChatId(
      currentUser.firebaseId,
      broadcasterFirebaseId,
    );

    // notification is null for live-streaming gifts (live_streaming_screen.dart:1425-1430 +
    // send_gift_bottom_sheet.dart:199-200).
    return cloudFunctionsService.sendGift(appTransaction, null, groupChatId);
  }
}

export const giftService = new GiftService();
